import type { ComponentType } from 'react'
import React, { useCallback, forwardRef, useState, useEffect, useMemo } from 'react'
import { withDependencies } from '@wix/thunderbolt-ioc'
import { useService } from '@wix/services-manager-react'
import { ContextProviderRegistrySymbol } from './symbols'
import type { ContextProviderRegistry } from './types'
import { Signal } from '@preact/signals-react'
import { useSignals } from '@preact/signals-react/runtime'
import { ContextBindingServiceDefinition } from './services'
import { ExperimentsSymbol } from '@wix/thunderbolt-symbols'
import type { Experiments } from '@wix/thunderbolt-symbols'

const isSignal = (value: any) => value instanceof Signal

export const ContextBindingWrapper = withDependencies(
	[ContextProviderRegistrySymbol, ExperimentsSymbol],
	(contextProviderRegistry: ContextProviderRegistry, experiments: Experiments) => {
		const servicesInfraExperiment = Boolean(experiments['specs.thunderbolt.servicesInfra'])
		const contextProvidersEnabled = Boolean(experiments['specs.thunderbolt.contextProviders'])

		const shouldWrap = servicesInfraExperiment && contextProvidersEnabled

		if (!shouldWrap) {
			return {
				wrapComponent(Component: ComponentType<any>) {
					return Component
				},
			}
		}

		return {
			wrapComponent: (Component: ComponentType<any>) => {
				return forwardRef<any, any>((props, ref) => {
					const contextBindingService = useService(ContextBindingServiceDefinition)
					const [, setTick] = useState(0)
					const forceUpdate = useCallback(() => setTick((tick) => tick + 1), [])

					useSignals()

					// subscribe to binding changes on mount and unsub on unmount
					useEffect(() => {
						return contextBindingService.subscribeToBindingChanges(props.compId, forceUpdate)
					}, [props.compId, forceUpdate, contextBindingService])

					const bindings = contextBindingService.getBindings(props.compId)

					const boundProps = bindings.reduce<Record<string, unknown>>((p, binding) => {
						const getContext = contextProviderRegistry.getUseContextHook(binding.contextProvider)
						const context = getContext()
						Object.entries(binding.properties).forEach(([compProp, contextFieldPath]) => {
							let contextValue: any = context
							for (const pathSegment of contextFieldPath.split('.')) {
								if (contextValue) {
									const maybeSignal = contextValue[pathSegment]
									contextValue = isSignal(maybeSignal) ? maybeSignal.value : maybeSignal
								}
							}
							p[compProp] = contextValue
						})
						return p
					}, {})

					return <Component {...props} {...boundProps} ref={ref} />
				})
			},
		}
	}
)
